import {
  idsIguais,
  getUsuarioLogadoAdaptado as getUsuarioLogado,
} from "../../utils/ids";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getEventos } from "../../services/eventosStore";
import {
  getTurmas,
  recarregarSeNecessario as recarregarTurmas,
} from "../../services/turmasStore";
import {
  getUsuarios,
  getNomeUsuario,
  recarregarSeNecessario as recarregarUsuarios,
} from "../../services/usuariosStore";
import { formatHeaderDate } from "../../utils/formatting";
import "./DetalheCalendario.scss";

function norm(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}

function parseLocalDateFromISO(isoString) {
  if (!isoString) return null;

  const raw = String(isoString).trim();

  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    const [year, month, day] = raw.slice(0, 10).split("-").map(Number);
    return new Date(year, month - 1, day);
  }

  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return null;

  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function parseDateParam(dateParam) {
  if (!dateParam) return null;

  const [year, month, day] = String(dateParam).split("-").map(Number);
  if (!year || !month || !day) return null;

  return new Date(year, month - 1, day);
}

function sameDay(a, b) {
  return (
    a &&
    b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function getDisciplinaIdsDoEvento(ev) {
  function toIdStr(v) {
    const s = String(v ?? "").trim();
    return s || null;
  }

  if (Array.isArray(ev?.disciplinaIds) && ev.disciplinaIds.length > 0) {
    return ev.disciplinaIds.map(toIdStr).filter(Boolean);
  }

  if (Array.isArray(ev?.turmasIds) && ev.turmasIds.length > 0) {
    return ev.turmasIds.map(toIdStr).filter(Boolean);
  }

  if (Array.isArray(ev?.disciplinaId)) {
    const arr = ev.disciplinaId.map(toIdStr).filter(Boolean);
    if (arr.length) return arr;
  }

  const candidates = [
    ev?.disciplinaCoordenadorId,
    ev?.disciplinacoordenadorId,
    ev?.disciplinCoordenadoraId,
    ev?.disciplinaCoordenadorID,
    ev?.disciplina_coordenador_id,
  ].filter((v) => v != null);

  for (const v of candidates) {
    if (Array.isArray(v)) {
      const arr = v.map(toIdStr).filter(Boolean);
      if (arr.length) return arr;
    } else {
      const s = toIdStr(v);
      if (s) return [s];
    }
  }

  const single = toIdStr(ev?.disciplinaId);
  return single ? [single] : [];
}

function getDisciplinasDoEvento(evento, disciplinas = []) {
  const ids = getDisciplinaIdsDoEvento(evento);

  if (!ids.length) return [];

  return ids.map((id) => {
    const disciplina = disciplinas.find((d) => idsIguais(d.id, id));
    const nome = String(disciplina?.nome || "").trim();
    const comp = String(disciplina?.complemento || "").trim();
    const label = nome ? (comp ? `${nome} - ${comp}` : nome) : `Turma ${id}`;
    return { id, nome: label };
  });
}

function getBarColor(evento, disciplinas = []) {
  const ids = getDisciplinaIdsDoEvento(evento);

  if (ids.length > 0) {
    const primeiraDisciplina = disciplinas.find((d) => idsIguais(d.id, ids[0]));

    if (primeiraDisciplina?.cor) {
      return primeiraDisciplina.cor;
    }
  }

  if (evento.destaque) return "#E97D7D";
  if (evento.calendario) return "#6FA8DC";
  return "#9AD18B";
}

export default function DetalheCalendario() {
  const navigate = useNavigate();
  const { date } = useParams();

  const busca = "";
  const [refreshKey, setRefreshKey] = useState(0);
  const [turmasCache, setTurmasCache] = useState(() => getTurmas());
  const [usuariosCache, setUsuariosCache] = useState(() => getUsuarios());

  const usuario = useMemo(() => getUsuarioLogado(), []);
  const instituicaoId = useMemo(
    () => usuario?.faculdadeId || usuario?.instituicaoId || null,
    [usuario],
  );

  useEffect(() => {
    const onEventosChanged = () => setRefreshKey((k) => k + 1);
    const onTurmasChanged = () => setTurmasCache(getTurmas(instituicaoId));
    const onUsuariosChanged = () =>
      setUsuariosCache(getUsuarios(instituicaoId));

    window.addEventListener("eventos:changed", onEventosChanged);
    window.addEventListener("storage", onEventosChanged);
    window.addEventListener("turmas:changed", onTurmasChanged);
    window.addEventListener("usuarios:changed", onUsuariosChanged);

    if (instituicaoId) {
      recarregarTurmas(instituicaoId).catch(() => {});
      recarregarUsuarios(instituicaoId).catch(() => {});
    }

    return () => {
      window.removeEventListener("eventos:changed", onEventosChanged);
      window.removeEventListener("storage", onEventosChanged);
      window.removeEventListener("turmas:changed", onTurmasChanged);
      window.removeEventListener("usuarios:changed", onUsuariosChanged);
    };
  }, [instituicaoId]);

  // Trocar coordenação no menu sai do dia expandido → volta para o calendário.
  // O evento do dia pode não pertencer à outra coordenação (igual ao editar).
  useEffect(() => {
    const onCoord = () => navigate("/ver-calendario");
    window.addEventListener("coordenacaoAtual:changed", onCoord);
    return () =>
      window.removeEventListener("coordenacaoAtual:changed", onCoord);
  }, [navigate]);

  const disciplinas = useMemo(() => turmasCache, [turmasCache]);

  const eventos = useMemo(() => {
    if (refreshKey < 0) return [];
    return getEventos([]);
  }, [refreshKey]);

  const dataSelecionada = useMemo(() => parseDateParam(date), [date]);

  const eventosDoDia = useMemo(() => {
    if (!dataSelecionada) return [];

    const termo = norm(busca);

    return eventos
      .filter((evento) => {
        if (!evento?.calendario) return false;

        const dataEvento = parseLocalDateFromISO(evento.dataEvento);
        return sameDay(dataEvento, dataSelecionada);
      })
      .map((evento) => ({
        ...evento,
        criadorNome: evento.criadoPorNome || getNomeUsuario(evento.criadoPorId),
        disciplinasEvento: getDisciplinasDoEvento(evento, disciplinas),
        barColor: getBarColor(evento, disciplinas),
      }))
      .filter((evento) => {
        if (!termo) return true;

        return (
          norm(evento.titulo).includes(termo) ||
          norm(evento.descricao).includes(termo) ||
          norm(evento.criadorNome).includes(termo) ||
          evento.disciplinasEvento.some((disciplina) =>
            norm(disciplina.nome).includes(termo),
          )
        );
      })
      .sort((a, b) => {
        const dateA = new Date(
          a.ultimaAtualizacao || a.dataEvento || 0,
        ).getTime();
        const dateB = new Date(
          b.ultimaAtualizacao || b.dataEvento || 0,
        ).getTime();
        return dateB - dateA;
      });
  }, [eventos, disciplinas, dataSelecionada, busca]);

  return (
    <div className="detalhe-calendario-page">
      <section className="detalhe-calendario-card detalhe-calendario-card--title">
        <h1 className="detalhe-calendario-title">Calendário</h1>
      </section>

      <section className="detalhe-calendario-card detalhe-calendario-card--lista">
        <div className="detalhe-calendario-header">
          <button
            type="button"
            className="detalhe-calendario-back"
            onClick={() => navigate("/ver-calendario")}
          >
            <span className="detalhe-calendario-back__icon">←</span>
            <span>Voltar</span>
          </button>

          <h2>{formatHeaderDate(dataSelecionada)}</h2>
        </div>

        <div className="detalhe-calendario-list">
          {eventosDoDia.length > 0 ? (
            eventosDoDia.map((evento) => (
              <article key={evento.id} className="detalhe-evento-card">
                <div
                  className="detalhe-evento-card__bar"
                  style={{ backgroundColor: evento.barColor }}
                />

                <div className="detalhe-evento-card__content">
                  <div className="detalhe-evento-card__top">
                    <h3>{evento.titulo}</h3>

                    <div className="chips-row">
                      {evento.disciplinasEvento.length > 0 ? (
                        evento.disciplinasEvento.map((disciplina) => (
                          <span
                            key={disciplina.id}
                            className="chip chip--turma"
                          >
                            {disciplina.nome}
                          </span>
                        ))
                      ) : (
                        <span className="detalhe-evento-card__sem-turma">
                          Nenhuma turma selecionada
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="detalhe-evento-card__autor">
                    <strong>Criado por:</strong> {evento.criadorNome}
                  </div>

                  <p className="detalhe-evento-card__descricao">
                    {evento.descricao}
                  </p>
                </div>
              </article>
            ))
          ) : (
            <div className="detalhe-calendario-empty">
              Nenhum evento encontrado para esta data.
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
